import type {
  Charity,
  CharityClosestStoresParams,
  CharityCourierSetupParams,
  CharityExpandable,
  CharityListOptions,
  CharityOpenStoresParams,
  PaginatedResponse,
  Store,
} from '@types'
import { apiRequest, paginatedRequest } from './base'

export const charityService = {
  /**
   * @roles public, api_key, captain
   */
  async list<E extends CharityExpandable = never>(
    options: CharityListOptions & { expand?: E | E[] } = {}
  ): Promise<PaginatedResponse<Charity>> {
    return paginatedRequest('/charities', { method: 'GET', params: options })
  },

  /**
   * @roles public, api_key, captain
   */
  async get<E extends CharityExpandable = never>(
    id: number,
    options: { expand?: E | E[] } = {}
  ): Promise<Charity> {
    return apiRequest(`/charities/${id}`, {
      method: 'GET',
      params: options.expand
        ? {
            expand: Array.isArray(options.expand)
              ? options.expand.join(',')
              : options.expand,
          }
        : {},
    })
  },

  /**
   * @roles charity, captain
   */
  async create(data: Partial<Charity>): Promise<Charity> {
    return apiRequest('/charities', { method: 'POST', body: data })
  },

  /**
   * @roles charity, captain
   */
  async update(id: number, data: Partial<Charity>): Promise<Charity> {
    return apiRequest(`/charities/${id}`, { method: 'PUT', body: data })
  },

  /**
   * @roles captain
   */
  async delete(id: number): Promise<void> {
    return apiRequest(`/charities/${id}`, { method: 'DELETE' })
  },

  /**
   * @roles captain
   */
  async restore(id: number): Promise<Charity> {
    return apiRequest(`/charities/${id}/restore`, { method: 'POST' })
  },

  /**
   * @roles driver, captain
   */
  async closestStores(
    id: string | number,
    options: CharityClosestStoresParams = {}
  ): Promise<Store[]> {
    return apiRequest(`/charities/${id}/closest_stores`, {
      method: 'GET',
      params: options,
    })
  },

  /**
   * @roles driver, captain
   */
  async openStores(
    id: string | number,
    options: CharityOpenStoresParams = {}
  ): Promise<Store[]> {
    return apiRequest(`/charities/${id}/open_stores`, {
      method: 'GET',
      params: options,
    })
  },

  /**
   * @roles captain
   */
  async courierSetup(
    id: string | number,
    data: CharityCourierSetupParams = {}
  ): Promise<Charity> {
    return apiRequest(`/charities/${id}/courier_setup`, {
      method: 'POST',
      body: data,
    })
  },
}
